import WidgetSkeleton from "../WidgetSkeleton";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Wallet, ArrowRight } from "lucide-react";
import { useBudgetStore } from "../../../budget/store/useBudgetStore";
import {
  getBudgetThresholdState,
  thresholdBarClass,
  thresholdTextClass,
} from "../../../budget/lib/budgetThreshold";

interface Props {
  year: number;
  month: number;
}

export default function BudgetWidget({ year, month }: Props) {
  const { budgetStatus, statusLoading, loadBudgetStatus } = useBudgetStore();

  useEffect(() => {
    loadBudgetStatus(year, month);
  }, [year, month, loadBudgetStatus]);

  if (statusLoading) {
    return <WidgetSkeleton progress />;
  }

  if (!budgetStatus) {
    return (
      <Link
        to="/budget"
        className="rounded-2xl bg-card border border-border p-4 sm:p-5 shadow-card flex items-center gap-3 hover:bg-muted/40 transition-colors group"
      >
        <div className="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Wallet className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-foreground">
            Set a monthly budget
          </p>
          <p className="hidden sm:block text-[12px] text-muted-foreground">
            Track spending against your limits
          </p>
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
      </Link>
    );
  }

  const { overallBudget, overallSpent, overallRemaining } = budgetStatus;
  const state = getBudgetThresholdState(overallSpent, overallBudget);
  const pct =
    overallBudget > 0 ? Math.min((overallSpent / overallBudget) * 100, 100) : 0;
  const fmt = (n: number) =>
    n.toLocaleString("th-TH", { minimumFractionDigits: 0 });

  return (
    <Link
      to="/budget"
      className="rounded-2xl bg-card border border-border p-4 sm:p-5 shadow-card hover:bg-muted/40 transition-colors group block"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
            <Wallet className="w-3.5 h-3.5 text-primary" />
          </div>
          <span className="text-[13px] font-semibold text-foreground">
            Monthly Budget
          </span>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
      </div>

      <div className="flex items-end justify-between mb-2">
        <div>
          <p className="text-[11px] text-muted-foreground mb-0.5">Spent</p>
          <p
            className={`text-[20px] font-bold [font-variant-numeric:tabular-nums] ${thresholdTextClass(state)}`}
          >
            ฿{fmt(overallSpent)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[11px] text-muted-foreground mb-0.5">Budget</p>
          <p className="text-[14px] font-semibold text-foreground [font-variant-numeric:tabular-nums]">
            ฿{fmt(overallBudget)}
          </p>
        </div>
      </div>

      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden mb-1.5">
        <div
          className={`h-full rounded-full transition-all duration-300 ${thresholdBarClass(state)}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex justify-between items-center">
        <span className="text-[11px] text-muted-foreground [font-variant-numeric:tabular-nums]">
          {pct.toFixed(0)}% used
        </span>
        <span
          className={`text-[12px] font-semibold [font-variant-numeric:tabular-nums] ${thresholdTextClass(state)}`}
        >
          {overallRemaining >= 0
            ? `฿${fmt(overallRemaining)} left`
            : `฿${fmt(Math.abs(overallRemaining))} over`}
        </span>
      </div>
    </Link>
  );
}
